import { BlockPreviewResult } from '../../../services/block-preview.service';

/**
 * What the preview panel shows (#263; task-cms-block-preview.md §5.4), from what the server answered. Pure; tested in
 * block-preview-state.spec.ts. The panel draws it; BlockPreviewController moves between states.
 *
 *   idle         nothing to show yet (no block active, no suburb picked): `message` is the hint
 *   loading      a request in flight: over the last render (updating), or "Rendering…" when there is none
 *   ready        the render, with its notes
 *   refused      422: the website would refuse the block; over the last render, dimmed
 *   missing      404: the page isn't in the preview service's copy of the website (the server says why)
 *   unavailable  424 unavailable/timeout, a network error or a server fault: Retry
 *   off          400 not configured: preview isn't set up on this server (the panel goes, the toggle turns off)
 *   failed       anything else the person can't retry their way out of (403, 413, a bad request, misconfigured,
 *                out of step)
 */
export type PreviewPhase =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'refused'
  | 'missing'
  | 'unavailable'
  | 'off'
  | 'failed';

export interface BlockPreviewState {
  phase: PreviewPhase;
  /** The last render, kept while updating and under an error so nothing flashes; null before the first. */
  html: string | null;
  notes: string[];
  /** What the panel says over (or instead of) the render; '' when the render speaks for itself. */
  message: string;
  /** Retry is offered. */
  retry: boolean;
}

export const IDLE_HINT = 'Click into a block to preview it.';
export const FIRST_RENDER = "Rendering with the website's own blocks…";

export function idleState(hint: string = IDLE_HINT): BlockPreviewState {
  return { phase: 'idle', html: null, notes: [], message: hint, retry: false };
}

/** A request went out: the last render stays (updating), or the first-render line shows. */
export function loadingState(prev: BlockPreviewState): BlockPreviewState {
  return {
    ...prev,
    phase: 'loading',
    message: prev.html ? '' : FIRST_RENDER,
    retry: false,
  };
}

export function readyState(result: BlockPreviewResult): BlockPreviewState {
  return {
    phase: 'ready',
    html: result.html,
    notes: result.notes ?? [],
    message: '',
    retry: false,
  };
}

/** The server's error envelope ({ error: { title, message[], html[], data } }) as a state over the last render. */
export function errorState(prev: BlockPreviewState, err: unknown): BlockPreviewState {
  const keep = { html: prev.html, notes: prev.html ? prev.notes : [] };
  const { phase, message } = classify(err);
  return { ...keep, phase, message, retry: phase === 'unavailable' };
}

const NOT_CONFIGURED = 'Preview Not Configured';
/** The two a Developer fixes (424, no Retry), and what they do. */
const DEVELOPER_FIXES: Record<string, string> = {
  'Preview Misconfigured': 'A Developer needs to check its settings.',
  'Preview Out Of Step':
    'A Developer needs to deploy matching versions of Conductor and the preview service.',
};

function classify(err: unknown): { phase: PreviewPhase; message: string } {
  const e: any = err;
  const status: number = typeof e?.status === 'number' ? e.status : 0;
  const body = e?.error?.error;
  const title: string = body?.title ?? '';
  const line = plain(body?.html?.[0] || (body?.message ?? []).join(' '));

  if (status === 422) return { phase: 'refused', message: refusal(body?.data, line) };
  // The server says why and what helps: the preview service's copy is refreshed by a Developer, not by publishing.
  if (status === 404) return { phase: 'missing', message: line || "This page isn't on the website yet." };
  if (status === 400 && title === NOT_CONFIGURED)
    return { phase: 'off', message: "Preview isn't set up on this server." };
  if (status === 403)
    return { phase: 'failed', message: 'Previewing is for Content Managers and Developers.' };
  if (status === 424 && DEVELOPER_FIXES[title])
    return {
      phase: 'failed',
      message: `${unavailable(reason(status, line))} ${DEVELOPER_FIXES[title]}`,
    };
  if (status === 400 || status === 413)
    return { phase: 'failed', message: line || "That can't be previewed." };
  return { phase: 'unavailable', message: unavailable(reason(status, line)) };
}

/**
 * "The website would refuse this block: …", with where (the issue paths, as the site's schema reports them). The
 * Worker's own message (data.message) is the reason; it opens with its own "The website would refuse block "…":",
 * which the line already says.
 */
function refusal(data: any, line: string): string {
  const reason =
    typeof data?.message === 'string'
      ? data.message.replace(/^The website would refuse block "[^"]*":\s*/, '')
      : '';
  const text = reason ? `${REFUSED}: ${reason}` : line || `${REFUSED}.`;
  const paths = (Array.isArray(data?.issues) ? data.issues : [])
    .map((i: any) => (Array.isArray(i?.path) ? i.path.join('.') : String(i?.path ?? '')))
    .filter((p: string) => !!p && !text.includes(p));
  return paths.length ? `${text} (at ${[...new Set(paths)].join(', ')})` : text;
}

const REFUSED = 'The website would refuse this block';

/** The server's html lines are HTML (handlebars escapes the values): shown as text, so tags go and entities decode. */
function plain(html: string): string {
  const entities: Record<string, string> = {
    amp: '&',
    lt: '<',
    gt: '>',
    quot: '"',
    '#39': "'",
    '#x27': "'",
  };
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&(amp|lt|gt|quot|#39|#x27);/g, (_m, e: string) => entities[e]);
}

function unavailable(why: string): string {
  return `Preview isn't available right now (${why}). Your edits aren't affected.`;
}

/** Why, in a few words: the server's line names the cause; with no answer at all, Conductor itself didn't answer. */
function reason(status: number, line: string): string {
  if (status === 0) return "Conductor didn't answer";
  const why = line
    .replace(/^The website preview service /, 'the preview service ')
    .replace(/\.?( Try again in a moment\.?)?$/, '');
  return why || `HTTP ${status}`;
}
